import React, { useEffect, useRef } from 'react'
import confetti from 'canvas-confetti'
import styled from 'styled-components'

const ConfettiContainer = styled.span`
  position: absolute;
  left: -12px;
  top: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 1;
`

const getPrefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Plays a confetti burst from the top of the modal when user scores 100% on a quiz.
const ConfettiBurst: React.FC = () => {
  const containerRef = useRef<HTMLSpanElement>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    if (getPrefersReducedMotion()) {
      return
    }

    const container = containerRef.current
    if (!container) {
      return
    }

    // Find the modal container to constrain confetti
    // Look for QuizModalContainer or any modal dialog
    let modalContainer = container.closest('[role="dialog"]') as HTMLElement
    if (!modalContainer) {
      // Fallback: look for the styled QuizModalContainer (has specific styles)
      modalContainer = container.closest(
        'div[class*="QuizModalContainer"]'
      ) as HTMLElement
    }
    if (!modalContainer) {
      // Last fallback: find any parent with modal-like styling
      let parent = container.parentElement
      while (parent) {
        const styles = window.getComputedStyle(parent)
        if (
          styles.position === 'relative' &&
          styles.overflow === 'hidden' &&
          parseInt(styles.width) > 400 &&
          parseInt(styles.height) > 500
        ) {
          modalContainer = parent
          break
        }
        parent = parent.parentElement
      }
    }
    if (!modalContainer) {
      return
    }

    // Create canvas element
    const canvas = document.createElement('canvas')
    canvasRef.current = canvas

    // Style the canvas to overlay the modal
    canvas.style.position = 'absolute'
    canvas.style.top = '0'
    canvas.style.left = '0'
    canvas.style.width = '100%'
    canvas.style.height = '100%'
    canvas.style.pointerEvents = 'none'
    canvas.style.zIndex = '100'

    // Get modal dimensions
    const modalRect = modalContainer.getBoundingClientRect()
    canvas.width = modalRect.width
    canvas.height = modalRect.height

    // Append canvas to modal
    modalContainer.appendChild(canvas)

    // Create confetti instance with the canvas
    const myConfetti = confetti.create(canvas, {
      resize: false,
      useWorker: false
    })

    // Small delay to ensure canvas is rendered
    requestAnimationFrame(() => {
      // Fire confetti from 15px from top-left corner
      const colors = [
        '#7DD3FC',
        '#C084FC',
        '#F9A8D4',
        '#FCD34D',
        '#FB923C',
        '#67E8F9'
      ]

      // Calculate position: 15px from top-left
      const x = 15 / modalRect.width
      const y = 15 / modalRect.height

      myConfetti({
        particleCount: 130,
        spread: 150,
        startVelocity: 30,
        origin: { x, y },
        colors,
        angle: 300,
        gravity: 0.5,
        drift: 0,
        ticks: 250,
        shapes: ['circle', 'square'],
        scalar: 1.3
      })
    })

    // Clean up after animation completes
    const timeoutId = setTimeout(() => {
      myConfetti.reset()
      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas)
      }
    }, 4000)

    return () => {
      clearTimeout(timeoutId)
      myConfetti.reset()
      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas)
      }
      canvasRef.current = null
    }
  }, [])

  return <ConfettiContainer ref={containerRef} aria-hidden='true' />
}

export default ConfettiBurst
